import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Header from '../components/layout/Header';
import { 
  Trophy, Crown, Calendar, Users, Gamepad2,
  DollarSign, CheckCircle2, ExternalLink, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChallengeHistory = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [challengesRes, winnersRes] = await Promise.all([
          axios.get(`${API}/challenges/ended`),
          axios.get(`${API}/winners`)
        ]);
        setChallenges(challengesRes.data);
        setWinners(winnersRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Group winners by challenge
  const getWinnersForChallenge = (challengeId) => {
    return winners
      .filter(w => w.challenge_id === challengeId)
      .sort((a, b) => (a.place || 1) - (b.place || 1));
  };

  const getPlaceBadge = (place) => {
    switch(place) {
      case 1: return { emoji: '🥇', text: '1st', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/50' };
      case 2: return { emoji: '🥈', text: '2nd', color: 'text-gray-300', bg: 'bg-gray-500/20', border: 'border-gray-400/50' };
      case 3: return { emoji: '🥉', text: '3rd', color: 'text-amber-500', bg: 'bg-amber-500/20', border: 'border-amber-500/50' };
      default: return { emoji: '🏆', text: '1st', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/50' };
    }
  };

  const getPrizeDisplay = (winner) => {
    if (winner.prize_info?.prize_type === 'money' || !winner.prize_info) {
      return <span className="text-green-400 font-bold">${winner.prize_info?.amount || winner.prize_amount || 0}</span>;
    } else if (winner.prize_info?.prize_type === 'game_code') {
      return <span className="text-blue-400 font-bold">🎮 Game Code</span>;
    } else if (winner.prize_info?.prize_type === 'item') {
      return <span className="text-purple-400 font-bold">🎁 Prize Item</span>;
    }
    return <span className="text-green-400 font-bold">${winner.prize_amount || 0}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <main className="pt-20 sm:pt-24 pb-12 px-3 sm:px-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500 mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <Calendar className="inline-block w-6 sm:w-10 h-6 sm:h-10 mr-2 text-red-500" />
            Challenge History
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">All completed challenges and their champions</p>
          <div className="flex justify-center gap-4 mt-4">
            <div className="px-4 py-2 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-2xl font-bold text-red-400">{challenges.length}</span>
              <span className="text-xs text-gray-500 ml-2">Challenges</span>
            </div>
            <div className="px-4 py-2 bg-gray-800/50 rounded-lg border border-gray-700">
              <span className="text-2xl font-bold text-yellow-400">{winners.length}</span>
              <span className="text-xs text-gray-500 ml-2">Winners</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full"></div>
          </div>
        ) : challenges.length === 0 ? (
          <Card className="glass-card p-12 text-center border-red-900/30">
            <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2 text-gray-400">No Ended Challenges Yet</h3>
            <p className="text-gray-500 mb-6">Completed challenges will appear here once they end.</p>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {challenges.map((challenge) => {
              const challengeWinners = getWinnersForChallenge(challenge.id);
              
              return (
                <Card 
                  key={challenge.id} 
                  className="glass-card overflow-hidden border-gray-700/50 hover:border-purple-500/30 transition-all"
                >
                  {/* Challenge Header with Game Cover */}
                  <div className="relative">
                    {/* Background blur from game cover */}
                    {challenge.game_cover_url && (
                      <div 
                        className="absolute inset-0 opacity-20"
                        style={{
                          backgroundImage: `url(${challenge.game_cover_url})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          filter: 'blur(20px)'
                        }}
                      />
                    )}
                    
                    <div className="relative p-4 sm:p-6 border-b border-gray-800">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Game Cover */}
                        {challenge.game_cover_url && (
                          <div className="flex-shrink-0">
                            <img 
                              src={challenge.game_cover_url} 
                              alt={challenge.game_name}
                              className="w-20 h-28 sm:w-24 sm:h-32 object-cover rounded-lg border-2 border-purple-500/50 shadow-lg shadow-purple-500/20"
                            />
                          </div>
                        )}
                        
                        {/* Challenge Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                            <div>
                              <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                {challenge.title}
                              </h2>
                              {challenge.game_name && (
                                <div className="flex items-center gap-2 mt-1">
                                  <Gamepad2 className="w-4 h-4 text-purple-400" />
                                  <span className="text-purple-400 text-sm">{challenge.game_name}</span>
                                </div>
                              )}
                            </div>
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-500/20 text-gray-400 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> ENDED
                            </span>
                          </div>
                          
                          {challenge.rules && (
                            <p className="text-gray-400 text-sm line-clamp-2 mb-3">{challenge.rules}</p>
                          )}
                          
                          {/* Stats Row */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-green-400" />
                              <span className="text-green-400 font-bold">${challenge.prize_amount}</span>
                              <span className="text-gray-500">prize pool</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-blue-400" />
                              <span className="text-blue-400 font-bold">{challenge.submission_count || 0}</span>
                              <span className="text-gray-500">submissions</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Crown className="w-4 h-4 text-yellow-400" />
                              <span className="text-yellow-400 font-bold">{challengeWinners.length}</span>
                              <span className="text-gray-500">winner{challengeWinners.length !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          
                          {/* Date Range */}
                          <div className="mt-3 text-xs text-gray-500">
                            📅 {formatDate(challenge.start_time)} → {formatDate(challenge.end_time)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Winners Section */}
                  {challengeWinners.length > 0 && (
                    <div className="p-4 sm:p-6 bg-gradient-to-b from-transparent to-yellow-500/5">
                      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Crown className="w-4 h-4 text-yellow-400" />
                        Champions
                      </h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {challengeWinners.map((winner) => {
                          const placeBadge = getPlaceBadge(winner.place);
                          
                          return (
                            <div 
                              key={winner.id}
                              className={`flex items-center gap-3 p-3 rounded-xl bg-black/40 border ${placeBadge.border} hover:bg-black/60 transition-all`}
                            >
                              {/* Place Badge */}
                              <div className={`flex-shrink-0 w-10 h-10 rounded-full ${placeBadge.bg} ${placeBadge.color} flex flex-col items-center justify-center`}>
                                <span className="text-base">{placeBadge.emoji}</span>
                              </div>
                              
                              {/* Profile */}
                              <img 
                                src={winner.profile_image_url || 'https://via.placeholder.com/40'} 
                                alt={winner.display_name}
                                className={`w-10 h-10 rounded-full border-2 ${placeBadge.border}`}
                              />
                              
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className={`font-bold ${placeBadge.color} truncate`}>
                                  {winner.display_name}
                                </div>
                                <div className="text-xs text-gray-500 truncate">@{winner.twitch_username}</div>
                                <div className="text-sm mt-0.5">{getPrizeDisplay(winner)}</div>
                              </div>
                              
                              {/* Watch Link */}
                              <a 
                                href={winner.video_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                                title="Watch winning video"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* No Winners Yet */}
                  {challengeWinners.length === 0 && (
                    <div className="p-6 text-center text-gray-500">
                      <Crown className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Winners not yet announced</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-red-900/20">
        <div className="max-w-7xl mx-auto text-center text-gray-600">
          <p className="flex items-center justify-center gap-2">
            <Trophy className="w-4 h-4 text-red-500" />
            CesarM Challenges
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ChallengeHistory;
